import React, { useState, useEffect } from 'react';
import { api, useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Edit2, UserX, UserCheck, Key, Loader2, RefreshCw, Users, ShieldCheck, Shield, User, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

function UserManagementPage() {
  const auth = useAuth();
  const currentUser = auth.user;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [editingUser, setEditingUser] = useState(null);
  const [processing, setProcessing] = useState(false);

  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showFormPassword, setShowFormPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);

  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formRole, setFormRole] = useState('SUPERVISOR');
  const [formActive, setFormActive] = useState(true);

  function fetchUsers() {
    setLoading(true);
    api.get('/users', { params: { include_inactive: showInactive } })
      .then(function(res) { setUsers(res.data); })
      .catch(function() { toast.error('Failed to load users'); })
      .finally(function() { setLoading(false); });
  }

  useEffect(function() { fetchUsers(); }, [showInactive]);

  function openCreateDialog() {
    setDialogMode('create');
    setEditingUser(null);
    setFormName('');
    setFormEmail('');
    setFormPassword('');
    setFormRole('SUPERVISOR');
    setFormActive(true);
    setShowFormPassword(false);
    setDialogOpen(true);
  }

  function openEditDialog(user) {
    setDialogMode('edit');
    setEditingUser(user);
    setFormName(user.name);
    setFormEmail(user.email);
    setFormPassword('');
    setFormRole(user.role);
    setFormActive(user.is_active);
    setShowFormPassword(false);
    setDialogOpen(true);
  }

  function openResetDialog(userId) {
    setResetUserId(userId);
    setNewPassword('');
    setShowResetPassword(false);
    setResetDialogOpen(true);
  }

  function getErrorMessage(err) {
    var detail = err.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail.map(function(e) { return e.msg || e.message || JSON.stringify(e); }).join(', ');
    }
    return 'Request failed';
  }

  function validatePassword(password) {
    if (!password || password.length < 8) return 'Password must be at least 8 characters long';
    if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
    if (!/\d/.test(password)) return 'Password must contain at least one number';
    return null;
  }

  function handleSave() {
    if (!formName || !formEmail) {
      toast.error('Name and Email are required');
      return;
    }
    if (dialogMode === 'create') {
      if (!formPassword) {
        toast.error('Password is required for new users');
        return;
      }
      var pwError = validatePassword(formPassword);
      if (pwError) {
        toast.error(pwError);
        return;
      }
    }

    setProcessing(true);
    if (dialogMode === 'create') {
      api.post('/users', { name: formName, email: formEmail, password: formPassword, role: formRole })
        .then(function() { toast.success('User created'); setDialogOpen(false); fetchUsers(); })
        .catch(function(err) { toast.error(getErrorMessage(err)); })
        .finally(function() { setProcessing(false); });
    } else {
      var payload = { name: formName, email: formEmail, role: formRole, is_active: formActive };
      api.put('/users/' + editingUser.id, payload)
        .then(function() { toast.success('User updated'); setDialogOpen(false); fetchUsers(); })
        .catch(function(err) { toast.error(getErrorMessage(err)); })
        .finally(function() { setProcessing(false); });
    }
  }

  function handleResetPassword() {
    var pwError = validatePassword(newPassword);
    if (pwError) {
      toast.error(pwError);
      return;
    }
    setProcessing(true);
    api.post('/users/' + resetUserId + '/reset-password', { new_password: newPassword })
      .then(function() { toast.success('Password reset successfully'); setResetDialogOpen(false); })
      .catch(function(err) { toast.error(getErrorMessage(err)); })
      .finally(function() { setProcessing(false); });
  }

  function handleDeactivate(userId) {
    if (!window.confirm('Deactivate this user? They will not be able to login.')) return;
    api.delete('/users/' + userId)
      .then(function() { toast.success('User deactivated'); fetchUsers(); })
      .catch(function(err) { toast.error(getErrorMessage(err)); });
  }

  function handleRestore(userId) {
    api.post('/users/' + userId + '/restore')
      .then(function() { toast.success('User restored'); fetchUsers(); })
      .catch(function(err) { toast.error(getErrorMessage(err)); });
  }

  function getRoleIcon(role) {
    if (role === 'OWNER') return React.createElement(ShieldCheck, { className: 'w-4 h-4' });
    if (role === 'MANAGER') return React.createElement(Shield, { className: 'w-4 h-4' });
    return React.createElement(User, { className: 'w-4 h-4' });
  }

  function getRoleBadgeClass(role) {
    if (role === 'OWNER') return 'bg-purple-100 text-purple-700 border-purple-200';
    if (role === 'MANAGER') return 'bg-blue-100 text-blue-700 border-blue-200';
    return 'bg-green-100 text-green-700 border-green-200';
  }

  var canCreateManagers = currentUser && currentUser.role === 'OWNER';

  return React.createElement('div', { className: 'space-y-6 animate-fade-in', 'data-testid': 'user-management-page' },
    React.createElement('div', { className: 'flex flex-col md:flex-row md:items-center justify-between gap-4' },
      React.createElement('div', null,
        React.createElement('h1', { className: 'font-heading text-3xl font-bold text-slate-900' }, 'User Management'),
        React.createElement('p', { className: 'text-slate-500' }, 'Manage user accounts and roles')
      ),
      React.createElement('div', { className: 'flex items-center gap-3' },
        React.createElement('div', { className: 'flex items-center gap-2' },
          React.createElement(Switch, { checked: showInactive, onCheckedChange: setShowInactive, id: 'show-inactive' }),
          React.createElement('label', { htmlFor: 'show-inactive', className: 'text-sm text-slate-600' }, 'Show Inactive')
        ),
        React.createElement(Button, { variant: 'outline', onClick: fetchUsers, 'data-testid': 'refresh-users-btn' },
          React.createElement(RefreshCw, { className: 'w-4 h-4 mr-2 ' + (loading ? 'animate-spin' : '') }), 'Refresh'),
        React.createElement(Button, { onClick: openCreateDialog, 'data-testid': 'add-user-btn' },
          React.createElement(Plus, { className: 'w-4 h-4 mr-2' }), 'Add User')
      )
    ),

    React.createElement(Card, { className: 'border-slate-200' },
      React.createElement(CardHeader, null,
        React.createElement(CardTitle, { className: 'flex items-center gap-2' },
          React.createElement(Users, { className: 'w-5 h-5' }), 'Users (', users.length, ')'
        ),
        React.createElement(CardDescription, null, 'Active users can login and access the system')
      ),
      React.createElement(CardContent, { className: 'p-0' },
        React.createElement(Table, { className: 'data-table' },
          React.createElement(TableHeader, null,
            React.createElement(TableRow, { className: 'bg-slate-50' },
              React.createElement(TableHead, null, 'Name'),
              React.createElement(TableHead, null, 'Email'),
              React.createElement(TableHead, null, 'Role'),
              React.createElement(TableHead, null, 'Status'),
              React.createElement(TableHead, null, 'Created'),
              React.createElement(TableHead, { className: 'w-40' }, 'Actions')
            )
          ),
          React.createElement(TableBody, null,
            loading ? React.createElement(TableRow, null,
              React.createElement(TableCell, { colSpan: 6, className: 'text-center py-8' },
                React.createElement(Loader2, { className: 'w-6 h-6 animate-spin mx-auto' }))
            ) :
            users.length === 0 ? React.createElement(TableRow, null,
              React.createElement(TableCell, { colSpan: 6, className: 'text-center py-12 text-slate-500' }, 'No users found')
            ) :
            users.map(function(u) {
              var isCurrentUser = currentUser && u.id === currentUser.id;
              var canEdit = currentUser && (currentUser.role === 'OWNER' || (currentUser.role === 'MANAGER' && u.role === 'SUPERVISOR'));
              
              return React.createElement(TableRow, { key: u.id, className: (u.is_active ? '' : 'bg-slate-50 opacity-60') + ' hover:bg-slate-50', 'data-testid': 'user-row-' + u.id },
                React.createElement(TableCell, { className: 'font-medium' },
                  u.name,
                  isCurrentUser && React.createElement('span', { className: 'ml-2 text-xs text-slate-400' }, '(you)')
                ),
                React.createElement(TableCell, { className: 'font-mono text-sm' }, u.email),
                React.createElement(TableCell, null,
                  React.createElement(Badge, { variant: 'outline', className: getRoleBadgeClass(u.role) + ' flex items-center gap-1 w-fit' },
                    getRoleIcon(u.role), u.role
                  )
                ),
                React.createElement(TableCell, null,
                  u.is_active ?
                    React.createElement('span', { className: 'inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700' },
                      React.createElement(UserCheck, { className: 'w-3 h-3' }), 'Active'
                    ) :
                    React.createElement('span', { className: 'inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700' },
                      React.createElement(UserX, { className: 'w-3 h-3' }), 'Inactive'
                    )
                ),
                React.createElement(TableCell, { className: 'text-sm text-slate-500' }, u.created_at ? u.created_at.split('T')[0] : '-'),
                React.createElement(TableCell, null,
                  React.createElement('div', { className: 'flex items-center gap-1' },
                    canEdit && React.createElement(Button, { variant: 'ghost', size: 'icon', onClick: function() { openEditDialog(u); }, className: 'h-9 w-9', title: 'Edit' },
                      React.createElement(Edit2, { className: 'w-4 h-4' })
                    ),
                    canEdit && !isCurrentUser && React.createElement(Button, { variant: 'ghost', size: 'icon', onClick: function() { openResetDialog(u.id); }, className: 'h-9 w-9', title: 'Reset Password' },
                      React.createElement(Key, { className: 'w-4 h-4' })
                    ),
                    currentUser && currentUser.role === 'OWNER' && !isCurrentUser && (
                      u.is_active ?
                        React.createElement(Button, { variant: 'ghost', size: 'icon', onClick: function() { handleDeactivate(u.id); }, className: 'h-9 w-9 text-red-500 hover:bg-red-50', title: 'Deactivate' },
                          React.createElement(UserX, { className: 'w-4 h-4' })
                        ) :
                        React.createElement(Button, { variant: 'ghost', size: 'icon', onClick: function() { handleRestore(u.id); }, className: 'h-9 w-9 text-green-500 hover:bg-green-50', title: 'Restore' },
                          React.createElement(UserCheck, { className: 'w-4 h-4' })
                        )
                    )
                  )
                )
              );
            })
          )
        )
      )
    ),

    React.createElement('div', { className: 'bg-slate-100 rounded-lg p-4' },
      React.createElement('h3', { className: 'font-medium text-slate-700 mb-2' }, 'Role Permissions'),
      React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-3 gap-4 text-sm' },
        React.createElement('div', { className: 'p-3 bg-white rounded border' },
          React.createElement('div', { className: 'flex items-center gap-2 font-medium text-purple-700 mb-1' },
            React.createElement(ShieldCheck, { className: 'w-4 h-4' }), 'Owner'
          ),
          React.createElement('p', { className: 'text-slate-600' }, 'Full access. Create/manage all users. View audit logs. Delete data.')
        ),
        React.createElement('div', { className: 'p-3 bg-white rounded border' },
          React.createElement('div', { className: 'flex items-center gap-2 font-medium text-blue-700 mb-1' },
            React.createElement(Shield, { className: 'w-4 h-4' }), 'Manager'
          ),
          React.createElement('p', { className: 'text-slate-600' }, 'Approve runs. Manage supervisors. View analytics. Manage master data.')
        ),
        React.createElement('div', { className: 'p-3 bg-white rounded border' },
          React.createElement('div', { className: 'flex items-center gap-2 font-medium text-green-700 mb-1' },
            React.createElement(User, { className: 'w-4 h-4' }), 'Supervisor'
          ),
          React.createElement('p', { className: 'text-slate-600' }, 'Create production runs. Submit for approval. View own data only.')
        )
      )
    ),

    React.createElement(Dialog, { open: dialogOpen, onOpenChange: setDialogOpen },
      React.createElement(DialogContent, { className: 'sm:max-w-md', 'data-testid': 'user-dialog' },
        React.createElement(DialogHeader, null,
          React.createElement(DialogTitle, null, dialogMode === 'create' ? 'Add New User' : 'Edit User'),
          React.createElement(DialogDescription, null, dialogMode === 'create' ? 'Create a new user account' : 'Update user details')
        ),
        React.createElement('div', { className: 'space-y-4 py-4' },
          React.createElement('div', { className: 'space-y-2' },
            React.createElement(Label, null, 'Full Name *'),
            React.createElement(Input, { value: formName, onChange: function(e) { setFormName(e.target.value); }, placeholder: 'John Doe', className: 'h-11', 'data-testid': 'input-user-name' })
          ),
          React.createElement('div', { className: 'space-y-2' },
            React.createElement(Label, null, 'Email *'),
            React.createElement(Input, { type: 'email', value: formEmail, onChange: function(e) { setFormEmail(e.target.value); }, placeholder: 'john@company.com', className: 'h-11', 'data-testid': 'input-user-email' })
          ),
          dialogMode === 'create' && React.createElement('div', { className: 'space-y-2' },
            React.createElement(Label, null, 'Password *'),
            React.createElement('div', { className: 'relative' },
              React.createElement(Input, { type: showFormPassword ? 'text' : 'password', value: formPassword, onChange: function(e) { setFormPassword(e.target.value); }, placeholder: 'Min 8 chars, upper, lower & number', className: 'h-11 pr-10', 'data-testid': 'input-user-password' }),
              React.createElement(Button, { type: 'button', variant: 'ghost', size: 'icon', className: 'absolute right-0 top-0 h-11 w-10 hover:bg-transparent', onClick: function() { setShowFormPassword(!showFormPassword); } },
                React.createElement(showFormPassword ? EyeOff : Eye, { className: 'w-4 h-4 text-slate-400' })
              )
            )
          ),
          React.createElement('div', { className: 'space-y-2' },
            React.createElement(Label, null, 'Role'),
            React.createElement(Select, { value: formRole, onValueChange: setFormRole },
              React.createElement(SelectTrigger, { className: 'h-11', 'data-testid': 'input-user-role' }, React.createElement(SelectValue, null)),
              React.createElement(SelectContent, null,
                React.createElement(SelectItem, { value: 'SUPERVISOR' }, 'Supervisor'),
                canCreateManagers && React.createElement(SelectItem, { value: 'MANAGER' }, 'Manager'),
                canCreateManagers && React.createElement(SelectItem, { value: 'OWNER' }, 'Owner')
              )
            )
          ),
          dialogMode === 'edit' && React.createElement('div', { className: 'flex items-center justify-between' },
            React.createElement(Label, null, 'Active'),
            React.createElement(Switch, { checked: formActive, onCheckedChange: setFormActive, disabled: editingUser && editingUser.id === currentUser?.id })
          )
        ),
        React.createElement(DialogFooter, null,
          React.createElement(Button, { variant: 'outline', onClick: function() { setDialogOpen(false); } }, 'Cancel'),
          React.createElement(Button, { onClick: handleSave, disabled: processing, 'data-testid': 'save-user-btn' },
            processing && React.createElement(Loader2, { className: 'w-4 h-4 mr-2 animate-spin' }),
            dialogMode === 'create' ? 'Create User' : 'Save Changes'
          )
        )
      )
    ),

    React.createElement(Dialog, { open: resetDialogOpen, onOpenChange: setResetDialogOpen },
      React.createElement(DialogContent, { className: 'sm:max-w-md', 'data-testid': 'reset-password-dialog' },
        React.createElement(DialogHeader, null,
          React.createElement(DialogTitle, { className: 'flex items-center gap-2' },
            React.createElement(Key, { className: 'w-5 h-5' }), 'Reset Password'
          ),
          React.createElement(DialogDescription, null, 'Enter a new password for this user')
        ),
        React.createElement('div', { className: 'space-y-4 py-4' },
          React.createElement('div', { className: 'space-y-2' },
            React.createElement(Label, null, 'New Password'),
            React.createElement('div', { className: 'relative' },
              React.createElement(Input, { type: showResetPassword ? 'text' : 'password', value: newPassword, onChange: function(e) { setNewPassword(e.target.value); }, placeholder: 'Min 8 chars, upper, lower & number', className: 'h-11 pr-10', 'data-testid': 'input-new-password' }),
              React.createElement(Button, { type: 'button', variant: 'ghost', size: 'icon', className: 'absolute right-0 top-0 h-11 w-10 hover:bg-transparent', onClick: function() { setShowResetPassword(!showResetPassword); } },
                React.createElement(showResetPassword ? EyeOff : Eye, { className: 'w-4 h-4 text-slate-400' })
              )
            )
          )
        ),
        React.createElement(DialogFooter, null,
          React.createElement(Button, { variant: 'outline', onClick: function() { setResetDialogOpen(false); } }, 'Cancel'),
          React.createElement(Button, { onClick: handleResetPassword, disabled: processing, 'data-testid': 'confirm-reset-btn' },
            processing && React.createElement(Loader2, { className: 'w-4 h-4 mr-2 animate-spin' }),
            'Reset Password'
          )
        )
      )
    )
  );
}

export default UserManagementPage;
